// app/javascript/controllers/home_navigation_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "blob", "logo", "videoWrapper", "scrollAnchor", "contentContainer",
    "blobHint", "contentBox",
    "mainContent", "topicCard", "topicTitle", "nextButton",
    "savoirFaireContent", "expertiseTitle", "expertiseCard", "finalHomeContent"
  ]
  
  connect() {
    this.animateEntry()
    this.scheduleBlobHint()
  }

  disconnect() {
    this.clearBlobHintTimers()
  }

  scheduleBlobHint() {
    if (!this.hasBlobHintTarget) return

    // Toujours partir caché (transition douce via classes)
    this.hideBlobHint()

    // Afficher après 5s, puis masquer après un court délai (si pas cliqué)
    this._blobHintShowTimer = setTimeout(() => {
      if (!this.hasBlobHintTarget) return
      this.showBlobHint()

      this._blobHintHideTimer = setTimeout(() => {
        this.hideBlobHint()
      }, 3500)
    }, 5000)
  }

  showBlobHint() {
    if (!this.hasBlobHintTarget) return
    this.blobHintTarget.classList.remove('opacity-0', '-translate-y-1')
    this.blobHintTarget.classList.add('opacity-100', 'translate-y-0')
  }

  hideBlobHint() {
    if (!this.hasBlobHintTarget) return
    this.blobHintTarget.classList.remove('opacity-100', 'translate-y-0')
    this.blobHintTarget.classList.add('opacity-0', '-translate-y-1')
  }

  clearBlobHintTimers() {
    if (this._blobHintShowTimer) {
      clearTimeout(this._blobHintShowTimer)
      this._blobHintShowTimer = null
    }
    if (this._blobHintHideTimer) {
      clearTimeout(this._blobHintHideTimer)
      this._blobHintHideTimer = null
    }
  }
  
  // Méthode pour gérer le clic sur "Découvrir"
  handleDiscoverClick(event) {
    event.preventDefault()
    this.transitionToMainContent()
  }
  
  transitionToMainContent() {
    // Phase 1: Fondu de sortie du contenu actuel
    this.contentContainerTarget.style.transition = "opacity 1s ease-out, transform 1s ease-out"
    this.contentContainerTarget.style.opacity = "0"
    this.contentContainerTarget.style.transform = "translateY(-50px) scale(0.95)"
    
    // Phase 2: Après le fondu, afficher le nouveau contenu
    setTimeout(() => {
      this.showMainContent()
    }, 1000)
  }
  
  showMainContent() {
    // Masquer complètement l'ancien contenu
    this.contentContainerTarget.style.display = "none"
    
    // Afficher le nouveau contenu principal
    if (this.hasMainContentTarget) {
      this.mainContentTarget.style.display = "block"
      
      // Attendre que le fondu de transition soit terminé avant de lancer les animations d'entrée
      setTimeout(() => {
        this.mainContentTarget.style.opacity = "1"
        this.mainContentTarget.style.transform = "translateY(0)"
        
        // Démarrer les animations d'entrée APRÈS la fin du fondu global
        setTimeout(() => {
          this.animateMainContentEntrance()
        }, 500) // 500ms après le début du fondu global
        
      }, 100)
    }
  }
  
  animateMainContentEntrance() {
    // D'abord préparer tous les éléments (les masquer)
    this.prepareAnimationElements()
    
    // Puis démarrer les animations après un court délai
    setTimeout(() => {
      this.startEntranceAnimations()
    }, 100)
  }
  
  prepareAnimationElements() {
    // Préparer le titre
    if (this.hasTopicTitleTarget) {
      this.topicTitleTarget.style.opacity = "0"
      this.topicTitleTarget.style.transform = "translateY(-100px)"
      this.topicTitleTarget.style.transition = "opacity 1s ease-out, transform 1s ease-out"
    }
    
    // Préparer les cartes de topics
    this.topicCardTargets.forEach((card, index) => {
      const position = index / (this.topicCardTargets.length - 1)
      
      let translateX = 0
      let translateY = 100
      
      // Déterminer la direction d'entrée basée sur la position
      if (position <= 0.2) {
        translateX = -150 // Gauche
      } else if (position <= 0.4) {
        translateX = -75  // Centre-gauche
      } else if (position <= 0.6) {
        translateX = 0    // Centre
      } else if (position <= 0.8) {
        translateX = 75   // Centre-droite
      } else {
        translateX = 150  // Droite
      }
      
      card.style.opacity = "0"
      card.style.transform = `translate(${translateX}px, ${translateY}px) scale(0.8)`
      card.style.transition = "opacity 0.8s ease-out, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)"
    })
    
    // Préparer le bouton Next
    if (this.hasNextButtonTarget) {
      this.nextButtonTarget.style.opacity = "0"
      this.nextButtonTarget.style.transform = "translateY(50px)"
      this.nextButtonTarget.style.transition = "opacity 0.6s ease-out, transform 0.6s ease-out"
    }
  }
  
  startEntranceAnimations() {
    // Animation du titre avec effet split-flap
    if (this.hasTopicTitleTarget) {
      setTimeout(() => {
        // Rendre le conteneur visible
        this.topicTitleTarget.style.opacity = "1"
        this.topicTitleTarget.style.transform = "translateY(0)"
        
        // Déclencher l'animation split-flap
        const splitFlapController = this.application.getControllerForElementAndIdentifier(
          this.topicTitleTarget, 
          'split-flap'
        )
        
        if (splitFlapController) {
          splitFlapController.start()
        }
      }, 200)
    }
    
    // Animation des cartes en cascade de gauche à droite
    this.topicCardTargets.forEach((card, index) => {
      const delay = 1400 + (index * 150) // Délai après l'animation du titre + 150ms par carte
      
      setTimeout(() => {
        card.style.opacity = "1"
        card.style.transform = "translate(0, 0) scale(1)"
        
        // Marquer l'animation comme terminée pour permettre les hovers
        setTimeout(() => {
          card.classList.add('entrance-complete')
        }, 800)
      }, delay)
    })
    
    // Animation du bouton Next en dernier
    if (this.hasNextButtonTarget) {
      const finalDelay = 1400 + (this.topicCardTargets.length * 150) + 300
      
      setTimeout(() => {
        this.nextButtonTarget.style.opacity = "1"
        this.nextButtonTarget.style.transform = "translateY(0)"
      }, finalDelay)
    }
  }
  
  animateEntry() {
    // Animation d'entrée du blob depuis le centre
    this.blobTarget.style.transform = "scale(0)"
    this.blobTarget.style.opacity = "0"
    
    // Apparition du blob avec une animation fluide
    setTimeout(() => {
      this.blobTarget.style.transition = "all 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)"
      this.blobTarget.style.transform = "scale(1)"
      this.blobTarget.style.opacity = "1"
    }, 100)
  }
  
  handleBlobClick() {
    // Masquer l'indication dès le premier clic
    this.clearBlobHintTimers()
    this.hideBlobHint()
    // Première phase : disparition du logo
    this.logoTarget.style.transition = "opacity .8s ease-out"
    this.logoTarget.style.opacity = "0"
    
    // Deuxième phase : expansion du blob après 800ms
    setTimeout(() => {
      this.expandBlob()
    }, 800)
  }
  
  expandBlob() {
    // Calcul de la taille finale nécessaire
    const windowDiagonal = Math.sqrt(
      Math.pow(window.innerWidth, 2) + Math.pow(window.innerHeight, 2)
    )
    const finalScale = (windowDiagonal * 1.5) / 150

    // Supprimer l'animation de pulsation
    this.blobTarget.classList.add('expanding')
    
    // Expansion progressive avec requestAnimationFrame
    this.smoothExpansion(finalScale)
  }
  
  smoothExpansion(finalScale) {
    const startTime = performance.now()
    const duration = 1000 // 1 seconde pour une expansion progressive
    const startScale = 1
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Fonction d'easing ease-out pour un effet naturel
      const easedProgress = 1 - Math.pow(1 - progress, 3)
      
      // Calcul du scale actuel
      const currentScale = startScale + (finalScale - startScale) * easedProgress
      
      // Application du transform
      this.blobTarget.style.transform = `scale(${currentScale})`
      
      // Continuer l'animation si pas terminée
      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        // Animation terminée
        this.onAnimationComplete()
      }
    }
    
    // Démarrer l'animation
    requestAnimationFrame(animate)
  }
  
  onAnimationComplete() {
    setTimeout(() => {
      this.showContent()
    }, 500)
  }
  
  showContent() {
    const svgContainer = document.querySelector('.line-animation-container')
    const contentContainer = document.querySelector('.content-container')

    // Sécurité: si l'indication est encore visible, on la masque
    this.clearBlobHintTimers()
    this.hideBlobHint()
    
    // Fondu du blob
    this.blobTarget.style.transition = "opacity 1.5s ease-out"
    this.blobTarget.style.opacity = "0"
    
    // Masquer le SVG et préparer l'affichage du contenu vidéo
    svgContainer.style.opacity = "0"
    contentContainer.style.display = "flex"
    
    // Lecture de la vidéo et fondu d'apparition
    if (this.hasVideoWrapperTarget) {
      const wrapper = this.videoWrapperTarget
      const videoElement = wrapper.querySelector('video')
      // Démarrer la vidéo si possible
      if (videoElement) {
        videoElement.play().catch(() => {/* silence l'erreur d'autoplay */})
      }
      // Déclencher le fondu d'apparition
      requestAnimationFrame(() => {
        wrapper.style.opacity = "1"
      })
    }
    
    // Affichage progressif du contenu texte
    setTimeout(() => {
      contentContainer.style.opacity = "1"
      contentContainer.style.transform = "translateY(0)"
    }, 300)
    
    // // Après 2s, décaler le contenu vers la gauche
    // setTimeout(() => {
    //   const contentBox = document.querySelector('.content-box')
    //   if (contentBox) {
    //     contentBox.style.transition = "transform 0.8s ease-out"
    //     contentBox.style.transform += " translateX(-24vw)"
    //   }
    // }, 1800)
    
    // Après la transition du blob, on le retire du flux pour éviter tout impact perf
    setTimeout(() => {
      this.blobTarget.style.display = "none"
    }, 1600)

    // Après 6s, le content-box disparaît, bouton Découvrir réapparaît en haut à gauche
    if (this.hasContentBoxTarget) {
      setTimeout(() => {
        // Créer le bouton flottant en haut à gauche
        const floatingBtn = document.createElement('div')
        floatingBtn.style.cssText = 'position: fixed; top: 1.5rem; left: 1.5rem; z-index: 50; opacity: 0; transition: opacity 0.8s ease;'
        floatingBtn.innerHTML = `
          <button class="bg-white/80 hover:bg-white text-gray-900 font-semibold py-2.5 px-6 rounded-lg backdrop-blur-sm shadow-lg border border-white/30 transition-all duration-300 hover:scale-105 hover:shadow-xl text-sm"
                  data-action="click->home-navigation#handleDiscoverClick">
            Découvrir →
          </button>
        `
        document.body.appendChild(floatingBtn)

        // Fondu de sortie du content-box (sans déplacement)
        this.contentBoxTarget.style.transition = 'opacity 1.5s ease-out'
        this.contentBoxTarget.style.opacity = '0'

        // Fondu d'entrée du bouton flottant
        setTimeout(() => {
          floatingBtn.style.opacity = '1'
        }, 1000)

        // Retirer le content-box du DOM après l'animation
        setTimeout(() => {
          this.contentBoxTarget.style.display = 'none'
        }, 1600)
      }, 6000)
    }
  }
  
  goToSavoirFaire(event) {
    event.preventDefault()

    // Continuer avec l'animation normale...
    if (this.hasMainContentTarget) {
      this.mainContentTarget.style.transition = "opacity 1s ease-out, transform 1s ease-out"
      this.mainContentTarget.style.opacity = "0"
      this.mainContentTarget.style.transform = "translate3d(0, -100vh, 0)"

      setTimeout(() => {
        this.mainContentTarget.style.display = "none"
        this.showSavoirFaireWithScrollEffect()
      }, 1000)
    }
  }

  showSavoirFaireWithScrollEffect() {
    if (this.hasSavoirFaireContentTarget) {
      // Préparer l'élément pour qu'il arrive depuis le bas
      this.savoirFaireContentTarget.style.display = "block" // S'assurer qu'il est visible
      this.savoirFaireContentTarget.style.opacity = "1"
      this.savoirFaireContentTarget.style.transform = "translate3d(0, 100vh, 0)" // Commence en bas avec GPU
      this.savoirFaireContentTarget.style.pointerEvents = "auto" // Réactiver les interactions
      this.savoirFaireContentTarget.style.position = "fixed" // S'assurer qu'il est visible

      // Forcer le reflow
      this.savoirFaireContentTarget.offsetHeight

      // Animer l'entrée depuis le bas
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.savoirFaireContentTarget.style.transition = "transform 1s cubic-bezier(0.25, 1, 0.5, 1)"
          this.savoirFaireContentTarget.style.transform = "translate3d(0, 0, 0)" // Remonte à sa position avec GPU

          // Démarrer les animations internes après l'arrivée
          setTimeout(() => {
            this.animateExpertiseContentEntrance()
          }, 500)
        }, 50)
      })
    }
  }

  animateExpertiseContentEntrance() {
    // Préparer les éléments
    this.prepareExpertiseAnimationElements()

    // Démarrer les animations après un court délai
    setTimeout(() => {
      this.startExpertiseEntranceAnimations()
    }, 100)
  }
  
  prepareExpertiseAnimationElements() {
    if (this.hasExpertiseTitleTarget) {
      this.expertiseTitleTarget.style.opacity = "0"
      this.expertiseTitleTarget.style.transform = "translateY(-100px)"
      this.expertiseTitleTarget.style.transition = "opacity 1s ease-out, transform 1s ease-out"
    }

    if (this.hasExpertiseCardTarget) {
      this.expertiseCardTargets.forEach((card, index) => {
        card.style.opacity = "0"
        card.style.transform = "translateY(100px) scale(0.8)"
        card.style.transition = "opacity 0.8s ease-out, transform 0.8s cubic-bezier(0.34, 1.56, 0.64, 1)"
      })
    }
  }

  startExpertiseEntranceAnimations() {
    if (this.hasExpertiseTitleTarget) {
      setTimeout(() => {
        this.expertiseTitleTarget.style.opacity = "1"
        this.expertiseTitleTarget.style.transform = "translateY(0)"

        this.startSplitFlapForExpertiseTitle()
      }, 200)
    }
    
    // Animation des cartes d'expertise en cascade
    if (this.hasExpertiseCardTarget) {
      this.expertiseCardTargets.forEach((card, index) => {
        const delay = 1400 + (index * 200) // Délai après l'animation du titre + 200ms par carte
        
        setTimeout(() => {
          card.style.opacity = "1"
          card.style.transform = "translateY(0) scale(1)"
          
          // Marquer l'animation comme terminée
          setTimeout(() => {
            card.classList.add('entrance-complete')
          }, 800)
        }, delay)
      })
    }
    
    // Animation du bouton Next en dernier
    if (this.hasSavoirFaireContentTarget) {
      const nextButton = this.savoirFaireContentTarget.querySelector('.next-btn')
      if (nextButton) {
        const finalDelay = 1400 + (this.expertiseCardTargets.length * 200) + 300
        
        setTimeout(() => {
          nextButton.style.opacity = "1"
          nextButton.style.transform = "translateY(0)"
        }, finalDelay)
      }
    }
  }

  // Démarre l'animation split-flap pour le titre d'expertise en réessayant
  // quelques fois si le contrôleur n'est pas encore connecté.
  startSplitFlapForExpertiseTitle(retryCount = 0) {
    if (!this.hasExpertiseTitleTarget) return

    const controller = this.application.getControllerForElementAndIdentifier(
      this.expertiseTitleTarget,
      'split-flap'
    )

    if (controller) {
      controller.start()
    } else if (retryCount < 10) {
      // Réessayer dans 100 ms (max 1 s)
      setTimeout(() => this.startSplitFlapForExpertiseTitle(retryCount + 1), 100)
    }
  }

  // MODIFICATION de la méthode goToFinalHome pour l'effet scroll vers le haut
  goToFinalHome(event) {
    event.preventDefault()

    // Transition de sortie du savoir-faire vers le haut
    if (this.hasSavoirFaireContentTarget) {
      this.savoirFaireContentTarget.style.transition = "opacity 1s ease-out, transform 1s cubic-bezier(0.25, 1, 0.5, 1)"
      this.savoirFaireContentTarget.style.opacity = "0"
      this.savoirFaireContentTarget.style.transform = "translate3d(0, -100vh, 0)" // Sort par le haut avec GPU

      setTimeout(() => {
        // Créer un effet de fondu vers le fond avant la redirection
        const body = document.body
        const fadeOverlay = document.createElement('div')
        fadeOverlay.style.cssText = `
          position: fixed;
          inset: 0;
          background: linear-gradient(to bottom, #0f172a, #1e293b, #0f172a);
          opacity: 0;
          z-index: 9999;
          transition: opacity 0.5s ease-in-out;
          pointer-events: none;
        `
        body.appendChild(fadeOverlay)

        // Animer le fondu
        requestAnimationFrame(() => {
          setTimeout(() => {
            fadeOverlay.style.opacity = '1'

            // Rediriger après le fondu
            setTimeout(() => {
              if (window.Turbo) {
                window.Turbo.visit('/accueil')
              } else {
                window.location.href = '/accueil'
              }
            }, 500)
          }, 50)
        })
      }, 800)
    }
  }

  // Quand on sort de l'animation (avant de visiter la page cible)
  cleanupSavoirFaireContainer() {
    if (this.hasSavoirFaireContentTarget) {
      this.savoirFaireContentTarget.style.position = 'static'
      this.savoirFaireContentTarget.style.pointerEvents = 'none'
    }
  }
}